import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api';

export interface Sight {
    id: number;
    name: string;
    slug: string;
    md5_hash?: string;
    icon: string;
    description?: string;
    order: number;
    widget_count?: number;
}

interface SightContextType {
    sights: Sight[];
    currentSight: Sight | null;
    isLoading: boolean;
    error: string | null;
    loadSights: (shop: string) => Promise<void>;
    refreshSightsSilent: (shop: string) => Promise<void>;
    getSight: (shop: string, identifier: string) => Promise<any>;
    createSight: (shop: string, data: any) => Promise<Sight>;
    updateSight: (shop: string, id: number, data: any) => Promise<void>;
    deleteSight: (shop: string, id: number) => Promise<void>;
    updateSightWidgets: (shop: string, id: number, widgets: any[]) => Promise<any>;
}

const SightContext = createContext<SightContextType | undefined>(undefined);

export const useSights = () => {
    const context = useContext(SightContext);
    if (!context) {
        throw new Error('useSights must be used within a SightProvider');
    }
    return context;
};

interface SightProviderProps {
    children: ReactNode;
}

export const SightProvider: React.FC<SightProviderProps> = ({ children }) => {
    const [sights, setSights] = useState<Sight[]>([]);
    const [currentSight, setCurrentSight] = useState<Sight | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const lastLoadedShopRef = useRef<string | null>(null);
    
    // Check if we're on a billing page - provide context but skip auto-loading
    const isBillingPage = typeof window !== 'undefined' &&
        (window.location.pathname === '/billing/choose-plan' ||
         window.location.pathname.includes('/billing/choose-plan'));

    const resetSightsState = useCallback(() => {
        setSights([]);
        setCurrentSight(null);
        setError(null);
        setIsLoading(false);
        lastLoadedShopRef.current = null;
    }, []);

    // Clear sights when user logs out in this or another tab.
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleStorage = (event: StorageEvent) => {
            if (event.key !== 'auth_event' || !event.newValue) return;
            try {
                const payload = JSON.parse(event.newValue);
                if (payload?.type === 'logout' || payload?.type === 'login') {
                    resetSightsState();
                }
            } catch {
                // ignore malformed payload
            }
        };

        // Clear sights immediately when a new login happens in the same tab
        const handleSameTabLogin = () => resetSightsState();

        window.addEventListener('storage', handleStorage);
        window.addEventListener('auth_login', handleSameTabLogin);
        return () => {
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener('auth_login', handleSameTabLogin);
        };
    }, [resetSightsState]);

    const loadSights = useCallback(async (shop: string) => {
        // Always clear stale data when shop changes or becomes empty.
        if (!shop) {
            resetSightsState();
            return;
        }

        if (lastLoadedShopRef.current !== shop) {
            setSights([]);
            setCurrentSight(null);
            setError(null);
            lastLoadedShopRef.current = shop;
        }

        // Don't load sights if on billing page
        if (isBillingPage) return;
        
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiGet<{ sights: Sight[] }>('/sights', { shop });
            setSights(response.sights);
        } catch (err: any) {
            // CRITICAL: Don't log errors if on billing page or if it's a 402 (Payment Required)
            // 402 errors are expected when there's no subscription
            const isBillingPage = typeof window !== 'undefined' && window.location.pathname === '/billing/choose-plan';
            const is402Error = err?.status === 402 || err?.message?.includes('402');
            const isBlocked = err?.message?.includes('API call blocked');
            
            if (!isBillingPage && !is402Error && !isBlocked) {
                setError('Failed to load sights');
                console.error('Error loading sights:', err);
            }
            // Never leave stale sights from a previous shop/user on screen.
            setSights([]);
            setCurrentSight(null);
            // If 402, blocked, or on billing page, silently fail - don't set error
        } finally {
            setIsLoading(false);
        }
    }, [isBillingPage, resetSightsState]);

    // Lightweight sights reload for small widget changes (no global loader / overlay)
    const refreshSightsSilent = useCallback(async (shop: string) => {
        if (!shop) {
            resetSightsState();
            return;
        }
        if (isBillingPage) return;
        if (lastLoadedShopRef.current !== shop) {
            setSights([]);
            setCurrentSight(null);
            setError(null);
            lastLoadedShopRef.current = shop;
        }
        try {
            const response = await apiGet<{ sights: Sight[] }>('/sights', { shop });
            setSights(response.sights);
        } catch (err: any) {
            const is402Error = err?.status === 402 || err?.message?.includes('402');
            const isBlocked = err?.message?.includes('API call blocked');
            if (!is402Error && !isBlocked) {
                console.error('Error silently refreshing sights:', err);
            }
            // Avoid showing stale sights if refresh fails for the current shop.
            setSights([]);
            setCurrentSight(null);
        }
    }, [isBillingPage, resetSightsState]);

    // Handle store switch events to clear and reload sights
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleStoreSwitch = (event: any) => {
            if (event.detail?.storeId) {
                // Get the active store from context or localStorage
                const activeStore = JSON.parse(localStorage.getItem('activeStore') || '{}');
                const shop = activeStore.shop;
                
                if (shop) {
                    // Reset sights state to clear previous store's data
                    resetSightsState();
                    // Load sights for the new store
                    loadSights(shop);
                }
            }
        };

        window.addEventListener('storeChanged', handleStoreSwitch);
        return () => window.removeEventListener('storeChanged', handleStoreSwitch);
    }, [resetSightsState, loadSights]);

    const getSight = useCallback(async (shop: string, identifier: string) => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiGet<any>(`/sights/${identifier}`, { shop });
            setCurrentSight(response.sight);
            return response;
        } catch (err) {
            setError('Failed to load sight');
            console.error('Error loading sight:', err);
            // Never keep a previous shop's currentSight if this fails.
            setCurrentSight(null);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const createSight = useCallback(async (shop: string, data: any): Promise<Sight> => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiPost<{ sight: Sight }>('/sights', {
                shop,
                ...data
            });

            // Reload sights after creation
            await loadSights(shop);

            return response.sight;
        } catch (err) {
            setError('Failed to create sight');
            console.error('Error creating sight:', err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [loadSights]);

    const updateSight = useCallback(async (shop: string, id: number, data: any) => {
        try {
            setIsLoading(true);
            setError(null);
            await apiPut(`/sights/${id}`, {
                shop,
                ...data
            });

            // Reload sights after update
            await loadSights(shop);
        } catch (err) {
            setError('Failed to update sight');
            console.error('Error updating sight:', err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [loadSights]);

    const deleteSight = useCallback(async (shop: string, id: number) => {
        try {
            setIsLoading(true);
            setError(null);
            await apiDelete(`/sights/${id}`, { shop });

            // Reload sights after deletion
            await loadSights(shop);
        } catch (err) {
            setError('Failed to delete sight');
            console.error('Error deleting sight:', err);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [loadSights]);

    const updateSightWidgets = useCallback(async (shop: string, id: number, widgets: any[]) => {
        try {
            // Do not flip global isLoading here to avoid full-page overlays
            // for small widget operations like removing a single card.
            setError(null);
            const response = await apiPost(`/sights/${id}/widgets`, {
                shop,
                widgets
            });

            // Reload sights after updating widgets to refresh widget_count in sidebar
            await loadSights(shop);
            
            // If the current sight is the one being updated, reload it to get updated widgets
            if (currentSight && currentSight.id === id) {
                await getSight(shop, currentSight.slug);
            }
            
            // Return response so caller can check for errors
            return response;
        } catch (err: any) {
            setError('Failed to update sight widgets');
            console.error('Error updating sight widgets:', err);
            // Return error response so caller can handle it
            throw err;
        }
    }, [loadSights, currentSight, getSight]);

    const value: SightContextType = {
        sights,
        currentSight,
        isLoading,
        error,
        loadSights,
        refreshSightsSilent,
        getSight,
        createSight,
        updateSight,
        deleteSight,
        updateSightWidgets,
    };

    return <SightContext.Provider value={value}>{children}</SightContext.Provider>;
};

